import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, GraduationCap, Home, Building2, Users,
  Award, Trophy, Star, CheckCircle, Lock, Play,
  Download, Clock, BookOpen
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

function BCard({ children, className = "", style = {} }) {
  return (
    <div className={`bg-white ${className}`} style={{ border: B.border, boxShadow: B.shadow, borderRadius: 0, ...style }}>
      {children}
    </div>
  );
}

function BBadge({ children, bg = "#686af3", color = "#fff" }) {
  return (
    <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
      style={{ fontFamily: B.mono, background: bg, color, border: "2px solid #2D3561" }}
    >{children}</span>
  );
}

const COURSE_BADGES = {
  familias: {
    id: "familia-preparada",
    name: "Familia Preparada",
    description: "Has completado el curso de preparación para familias con animales",
    icon: Home,
    bg: "#fa9000",
  },
  protectoras: {
    id: "protectora-certificada",
    name: "Protectora Certificada",
    description: "Has completado el curso de gestión de emergencias en refugios",
    icon: Building2,
    bg: "#686af3",
  },
  voluntarios: {
    id: "voluntario-experto",
    name: "Voluntario Experto",
    description: "Has completado el curso avanzado de voluntariado en emergencias",
    icon: Users,
    bg: "#a88efb",
  }
};

const COURSES = [
  { id: "familias", title: "Familias", subtitle: "Tu plan, tus animales", icon: Home, bg: "#fa9000", lessons: 8, duration: "4-6 horas", available: true },
  { id: "protectoras", title: "Protectoras", subtitle: "Gestión de emergencias en refugios", icon: Building2, bg: "#686af3", lessons: 10, duration: "8-10 horas", available: false },
  { id: "voluntarios", title: "Voluntarios", subtitle: "Formación para ayudar en emergencias", icon: Users, bg: "#a88efb", lessons: 12, duration: "10-12 horas", available: false },
];

export default function AprendePanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coursesProgress, setCoursesProgress] = useState({});
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) loadAllProgress();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAllProgress = async () => {
    try {
      const token = localStorage.getItem("ea_token");
      const progressPromises = COURSES.map(course =>
        fetch(`${API}/aprende/progress/${course.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.ok ? res.json() : null).catch(() => null)
      );
      const results = await Promise.all(progressPromises);
      const progress = {};
      const badges = [];
      COURSES.forEach((course, idx) => {
        if (results[idx]) {
          progress[course.id] = results[idx];
          if (results[idx].completed_at && COURSE_BADGES[course.id]) {
            badges.push({ ...COURSE_BADGES[course.id], completedAt: results[idx].completed_at, certificateId: results[idx].certificate_id });
          }
        }
      });
      setCoursesProgress(progress);
      setEarnedBadges(badges);
    } catch (e) {
      console.error("Error loading progress", e);
    } finally { setLoading(false); }
  };

  const downloadCertificate = async (courseId) => {
    try {
      const token = localStorage.getItem("ea_token");
      const res = await fetch(`${API}/aprende/certificate/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `certificado_${courseId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Certificado descargado!");
      }
    } catch { toast.error("Error al descargar el certificado"); }
  };

  const getProgressPercent = (courseId) => {
    const p = coursesProgress[courseId];
    if (!p) return 0;
    const course = COURSES.find(c => c.id === courseId);
    return Math.round((p.completed_lessons?.length || 0) / course.lessons * 100);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#f8f7f4", fontFamily: B.font }}>
        <BCard className="p-8 text-center">
          <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: "#a88efb" }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D3561" }}>ACCESO REQUERIDO</h2>
          <p className="text-sm mb-4" style={{ color: "#999", fontFamily: B.mono }}>Inicia sesión para ver tu formación</p>
          <button
            onClick={() => navigate("/auth")}
            className="px-6 py-2 font-bold text-sm uppercase tracking-wider transition-all hover:-translate-y-0.5"
            style={{ fontFamily: B.font, background: "#686af3", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
          >
            INICIAR SESIÓN
          </button>
        </BCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="aprende-panel" style={{ background: "#f8f7f4", fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: "#2D3561", borderBottom: B.border }}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/aprende" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider transition-colors hover:opacity-80" style={{ fontFamily: B.mono, color: "#f8f7f4" }}>
              <ArrowLeft className="w-4 h-4" />
              APRENDE
            </Link>
            <h1 className="text-lg font-bold tracking-tight" style={{ color: "#f8f7f4" }}>MI FORMACIÓN</h1>
            <div className="w-20" />
          </div>
        </div>
      </header>

      {/* Badges Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold tracking-tight mb-6 flex items-center gap-2" style={{ color: "#2D3561" }}>
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#a88efb", border: "2px solid #2D3561" }}>
              <Trophy className="w-4 h-4 text-white" />
            </div>
            MIS LOGROS
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8" style={{ border: "3px solid #a88efb", borderTopColor: "transparent", animation: "spin 1s linear infinite" }} />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {Object.entries(COURSE_BADGES).map(([courseId, badge]) => {
                const earned = earnedBadges.find(b => b.id === badge.id);
                const BadgeIcon = badge.icon;
                return (
                  <motion.div key={badge.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={earned ? { y: -4 } : {}}>
                    <BCard className={`p-6 text-center ${!earned ? "opacity-40" : ""}`} style={{ boxShadow: earned ? B.shadow : B.shadowSm }}>
                      <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ background: earned ? badge.bg : "#ccc", border: B.border }}>
                        {earned ? <BadgeIcon className="w-10 h-10 text-white" /> : <Lock className="w-8 h-8" style={{ color: "#999" }} />}
                      </div>
                      <h3 className="font-bold text-base mb-2" style={{ color: earned ? "#2D3561" : "#999" }}>{badge.name}</h3>
                      <p className="text-xs mb-4" style={{ fontFamily: B.mono, color: "#999" }}>{badge.description}</p>
                      {earned ? (
                        <div className="space-y-3">
                          <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#8B9A2D" }}>
                            OBTENIDO {new Date(earned.completedAt).toLocaleDateString("es-ES")}
                          </p>
                          <button
                            onClick={() => downloadCertificate(courseId)}
                            className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center gap-1.5 mx-auto"
                            style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}
                            data-testid={`download-cert-${courseId}`}
                          >
                            <Download className="w-3.5 h-3.5" /> CERTIFICADO
                          </button>
                        </div>
                      ) : (
                        <BBadge bg="#ccc" color="#666">BLOQUEADO</BBadge>
                      )}
                    </BCard>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Stats */}
          {earnedBadges.length > 0 && (
            <BCard className="p-6 mb-12" style={{ background: "#a88efb10" }}>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold" style={{ color: "#2D3561" }}>{earnedBadges.length}</p>
                  <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: "#a88efb" }}>BADGES</p>
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ color: "#2D3561" }}>
                    {Object.values(coursesProgress).reduce((acc, p) => acc + (p.completed_lessons?.length || 0), 0)}
                  </p>
                  <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: "#a88efb" }}>LECCIONES</p>
                </div>
                <div>
                  <p className="text-3xl font-bold" style={{ color: "#2D3561" }}>
                    {earnedBadges.length}/{Object.keys(COURSE_BADGES).length}
                  </p>
                  <p className="text-[10px] tracking-widest font-bold" style={{ fontFamily: B.mono, color: "#a88efb" }}>CURSOS</p>
                </div>
              </div>
            </BCard>
          )}

          {/* Courses List */}
          <h2 className="text-xl font-bold tracking-tight mb-6 flex items-center gap-2" style={{ color: "#2D3561" }}>
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: "#686af3", border: "2px solid #2D3561" }}>
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            MIS CURSOS
          </h2>

          <div className="space-y-4">
            {COURSES.map((course, idx) => {
              const progress = coursesProgress[course.id];
              const percent = getProgressPercent(course.id);
              const isCompleted = progress?.completed_at;
              const CourseIcon = course.icon;
              return (
                <motion.div key={course.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }}>
                  <BCard className={`p-5 ${!course.available ? "opacity-50" : ""}`} style={{ boxShadow: isCompleted ? "none" : B.shadow }}>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 flex items-center justify-center flex-shrink-0" style={{ background: isCompleted ? "#8B9A2D" : course.bg, border: "2px solid #2D3561" }}>
                        {isCompleted ? <CheckCircle className="w-7 h-7 text-white" /> : <CourseIcon className="w-7 h-7 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold" style={{ color: "#2D3561" }}>{course.title}</h3>
                          {isCompleted && <BBadge bg="#8B9A2D">COMPLETADO</BBadge>}
                          {!course.available && <BBadge bg="#ccc" color="#666">PRÓXIMAMENTE</BBadge>}
                        </div>
                        <p className="text-xs mb-2" style={{ fontFamily: B.mono, color: "#999" }}>{course.subtitle}</p>
                        {course.available && (
                          <div className="flex items-center gap-4">
                            <div className="flex-1 h-2" style={{ background: "#f8f7f4", border: "1px solid #2D3561" }}>
                              <div className="h-full transition-all" style={{ width: `${percent}%`, background: isCompleted ? "#8B9A2D" : course.bg }} />
                            </div>
                            <span className="text-xs font-bold" style={{ fontFamily: B.mono, color: "#2D3561" }}>{percent}%</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right mr-3 flex-shrink-0">
                        <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#999" }}>{course.lessons} LECCIONES</p>
                        <p className="text-[10px] tracking-wider" style={{ fontFamily: B.mono, color: "#ccc" }}>{course.duration}</p>
                      </div>
                      {course.available ? (
                        <button
                          onClick={() => navigate(`/aprende/curso/${course.id}`)}
                          className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 flex items-center gap-1.5 flex-shrink-0"
                          style={{ fontFamily: B.mono, background: isCompleted ? "#8B9A2D" : course.bg, color: "#fff", border: B.border, boxShadow: B.shadowSm }}
                          data-testid={`course-btn-${course.id}`}
                        >
                          {isCompleted ? <><Award className="w-3.5 h-3.5" /> VER</> : percent > 0 ? <><Play className="w-3.5 h-3.5" /> CONTINUAR</> : <><Play className="w-3.5 h-3.5" /> EMPEZAR</>}
                        </button>
                      ) : (
                        <button disabled className="px-4 py-2 text-xs font-bold uppercase tracking-wider opacity-40 flex items-center gap-1.5 flex-shrink-0"
                          style={{ fontFamily: B.mono, background: "#fff", color: "#999", border: "2px solid #ccc" }}>
                          <Lock className="w-3.5 h-3.5" /> BLOQUEADO
                        </button>
                      )}
                    </div>
                  </BCard>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
